import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowLeft, Calendar, MapPin, Check, Clock, AlertTriangle } from 'lucide-react';

const MOCK_STEPS = [
  {
    id: 'select',
    title: 'Step 1: Create Your Event',
    description: 'Give your event a name and pick a city. This is where coordination starts.',
    content: (
      <div className="space-y-3">
        <div className="p-3 bg-slate-50 rounded-lg border">
          <p className="text-sm font-medium text-slate-700">Annual Pride Gala</p>
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> Austin, TX</p>
        </div>
      </div>
    ),
  },
  {
    id: 'hold1',
    title: 'Step 2: Place Your First Hold',
    description: 'Pick a date. This tentatively reserves it while you plan. You get up to 3 holds per event.',
    content: (
      <div className="space-y-3">
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-1 h-10 bg-emerald-500 rounded-full" />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">Hold 1 — Primary</p>
              <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="h-3 w-3" /> Saturday, Jun 15, 2026</p>
            </div>
            <div className="px-2 py-1 bg-emerald-100 rounded-full">
              <span className="text-xs font-medium text-emerald-700">Active</span>
            </div>
          </div>
        </div>
        <div className="p-3 bg-slate-50 rounded-lg border border-dashed border-slate-300">
          <p className="text-xs text-slate-400 text-center">Hold 2 — Available</p>
        </div>
        <div className="p-3 bg-slate-50 rounded-lg border border-dashed border-slate-300">
          <p className="text-xs text-slate-400 text-center">Hold 3 — Available</p>
        </div>
      </div>
    ),
  },
  {
    id: 'conflict',
    title: 'Step 3: Conflict Detection',
    description: 'If another planner holds the same date in your city, PlanBlox alerts both of you automatically.',
    content: (
      <div className="space-y-3">
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-1 h-10 bg-emerald-500 rounded-full" />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">Your Hold — Jun 15</p>
              <p className="text-xs text-slate-500">Annual Pride Gala · Austin, TX</p>
            </div>
            <Check className="h-4 w-4 text-emerald-600" />
          </div>
        </div>
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Overlap Detected!</p>
              <p className="text-xs text-amber-600">Another planner has Jun 15 held in Austin, TX</p>
            </div>
          </div>
        </div>
        <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-xs text-blue-700">You can message the other planner to coordinate, or move your hold to a different date.</p>
        </div>
      </div>
    ),
  },
  {
    id: 'publish',
    title: 'Step 4: Confirm & Publish',
    description: 'Once your date is final, publish your event. It goes live on the Public Calendar for the community.',
    content: (
      <div className="space-y-3">
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-1 h-10 bg-emerald-500 rounded-full" />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">Annual Pride Gala</p>
              <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="h-3 w-3" /> Saturday, Jun 15, 2026 · Austin, TX</p>
            </div>
            <div className="px-2 py-1 bg-emerald-500 rounded-full">
              <span className="text-xs font-medium text-white">Published</span>
            </div>
          </div>
        </div>
        <div className="p-3 bg-slate-50 rounded-lg border text-center">
          <p className="text-xs text-slate-600">Your event is now visible to the community and other planners.</p>
          <div className="flex items-center justify-center gap-1 mt-1.5">
            <Clock className="h-3 w-3 text-emerald-500" />
            <span className="text-xs text-emerald-600 font-medium">Live on Public Calendar</span>
          </div>
        </div>
      </div>
    ),
  },
];

const HoldsDemo = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const current = MOCK_STEPS[step];

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto">
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={onClose}
          className="mb-4 text-muted-foreground"
          data-testid="holds-demo-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Preview
        </Button>

        <div className="mb-6">
          <h2 className="text-xl font-bold">How Holds Work</h2>
          <p className="text-sm text-muted-foreground mt-1">See how PlanBlox helps you coordinate event dates</p>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5 mb-6">
          {MOCK_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? 'bg-emerald-500' : 'bg-slate-200'}`}
            />
          ))}
        </div>

        {/* Step Content */}
        <Card className="mb-6">
          <CardContent className="p-5">
            <h3 className="font-semibold text-base mb-1">{current.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{current.description}</p>
            {current.content}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            data-testid="holds-demo-prev-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          {step < MOCK_STEPS.length - 1 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="holds-demo-next-btn"
            >
              Next Step
            </Button>
          ) : (
            <Button
              onClick={onClose}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="holds-demo-done-btn"
            >
              Got it!
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HoldsDemo;
